import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth, useOrganization } from '@clerk/clerk-react'

import { QUERY_GC_TIME_MS, QUERY_STALE_TIME_MS } from '@/config/constants'
import { snippetKeys } from '@/lib/queryKeys'
import { requireAuthToken } from '@/lib/api'

import {
  deleteSnippetVariant,
  fetchSnippetVariants,
  putSnippetVariant,
} from '../api'
import type { PutSnippetVariantInput } from '../types'
import { useInvalidateSnippets } from './useSnippets'

/**
 * The duties that have customised one snippet.
 *
 * The sheet hook owns this query, so it runs whichever tab is showing — the
 * Variants panel is not force-mounted, but the fetch does not wait for it.
 * `enabled` is what keeps it off the sheets that have no Variants tab at all.
 */
export function useSnippetVariantsQuery(
  snippetId: string | null,
  enabled = true
) {
  const { getToken } = useAuth()
  const { organization } = useOrganization()

  return useQuery({
    queryKey: snippetKeys.variants(organization?.id, snippetId ?? undefined),
    queryFn: async ({ signal }) =>
      fetchSnippetVariants(
        await requireAuthToken(getToken),
        snippetId!,
        signal
      ),
    enabled: enabled && Boolean(organization?.id) && Boolean(snippetId),
    staleTime: QUERY_STALE_TIME_MS,
    gcTime: QUERY_GC_TIME_MS,
    refetchOnWindowFocus: false,
  })
}

export function usePutSnippetVariantMutation() {
  const { getToken } = useAuth()
  const invalidate = useInvalidateSnippets()

  return useMutation({
    mutationFn: async ({
      snippetId,
      dutyId,
      ...input
    }: PutSnippetVariantInput & { snippetId: string; dutyId: string }) =>
      putSnippetVariant(
        await requireAuthToken(getToken),
        snippetId,
        dutyId,
        input
      ),
    onSuccess: (_data, { dutyId }) => invalidate(dutyId),
  })
}

export function useDeleteSnippetVariantMutation() {
  const { getToken } = useAuth()
  const invalidate = useInvalidateSnippets()

  return useMutation({
    mutationFn: async ({
      snippetId,
      dutyId,
    }: {
      snippetId: string
      dutyId: string
    }) =>
      deleteSnippetVariant(await requireAuthToken(getToken), snippetId, dutyId),
    onSuccess: (_data, { dutyId }) => invalidate(dutyId),
  })
}
